import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http, formatErr } from "@/lib/api";
import { ClipboardText, ArrowRight, CheckCircle, WarningCircle, Spinner } from "@phosphor-icons/react";

export default function Register() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [phase, setPhase] = useState("loading"); // loading | invalid | form | done
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState("");

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let active = true;
    if (!token) { setPhase("invalid"); setLoadErr("No invite token provided."); return; }
    (async () => {
      try {
        const r = await http.get(`/invites/validate/${token}`);
        if (!active) return;
        setInvite(r.data);
        setName(r.data.name || "");
        setPhase("form");
      } catch (ex) {
        if (!active) return;
        setLoadErr(formatErr(ex?.response?.data?.detail));
        setPhase("invalid");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await http.post("/invites/accept", { token, name, password, phone, address });
      setPhase("done");
    } catch (ex) {
      setErr(formatErr(ex?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6 justify-center">
          <div className="h-8 w-8 rounded-sm flex items-center justify-center" style={{ background: "#002FA7" }}>
            <ClipboardText size={20} weight="bold" color="#fff" />
          </div>
          <div className="font-heading font-semibold tracking-tight text-lg">ServinPri</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-7" data-testid="register-card">
          {phase === "loading" && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center" data-testid="register-loading">
              <Spinner size={18} className="animate-spin" /> Checking your invite…
            </div>
          )}

          {phase === "invalid" && (
            <div className="text-center py-4" data-testid="register-invalid">
              <WarningCircle size={36} weight="duotone" className="mx-auto text-amber-500 mb-3" />
              <h1 className="text-xl font-heading font-semibold tracking-tight">Invite not valid</h1>
              <p className="text-sm text-slate-500 mt-2">{loadErr}</p>
              <Link to="/login" className="inline-block mt-4 text-sm text-[#002FA7] hover:underline">← Back to sign in</Link>
            </div>
          )}

          {phase === "done" && (
            <div className="text-center py-4" data-testid="register-done">
              <CheckCircle size={40} weight="fill" className="mx-auto text-emerald-500 mb-3" />
              <h1 className="text-xl font-heading font-semibold tracking-tight">Registration complete</h1>
              <p className="text-sm text-slate-600 mt-2">Your account has been created and is <strong>awaiting administrator approval</strong>. You will be able to sign in once it is approved — we will email you.</p>
              <Link to="/login" data-testid="register-to-login" className="inline-flex items-center gap-1 mt-5 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
                Go to sign in <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          )}

          {phase === "form" && invite && (
            <>
              <div className="overline">Complete your registration</div>
              <h1 className="text-2xl font-heading font-semibold tracking-tight mt-1">Set up your account</h1>
              <p className="text-sm text-slate-500 mt-1.5">
                Invited as <span className="font-medium text-slate-700">{invite.role_label}</span>
              </p>

              <form onSubmit={submit} className="space-y-4 mt-5">
                <div>
                  <label className="overline mb-1.5 block">Email</label>
                  <input value={invite.email} disabled data-testid="register-email" className="rmms-input bg-slate-50 text-slate-500" />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Full name *</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="rmms-input" />
                </div>
                <div>
                  <label className="overline mb-1.5 block">Password * <span className="text-slate-400 normal-case tracking-normal">(min 6 chars)</span></label>
                  <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="rmms-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline mb-1.5 block">Phone</label>
                    <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="register-phone" className="rmms-input" />
                  </div>
                  <div>
                    <label className="overline mb-1.5 block">Address</label>
                    <input value={address} onChange={(e) => setAddress(e.target.value)} data-testid="register-address" className="rmms-input" />
                  </div>
                </div>
                {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1" data-testid="register-error">{err}</div>}
                <button type="submit" disabled={busy} data-testid="register-submit"
                  className="w-full h-11 rounded-md text-white font-medium text-sm flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#002FA7" }}>
                  {busy ? "Submitting…" : <>Create account <ArrowRight size={16} weight="bold" /></>}
                </button>
                <p className="text-[11px] text-slate-400 text-center">An administrator will review and approve your account before you can sign in.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
